<template>
    <ScrollPanel style="height: 450px;" class="border-round-xl">
        <div class="mx-3">
            <div v-if="isEmptyChat" class="justify-content-center">
                <div style="margin-top: 2em; text-align: center;">
    <i :class="['pi', 'pi-envelope']" style="font-size: 1.5rem; display: block; margin: 0 auto;" />
    <div style="margin-top: 2em; text-align: center;">
        {{ $t('EMPTYMSG') }}
    </div>
</div>

            </div>
            <div v-for="message in chatThread" :key="message" class=".card message_container mt-4"
                :style="{ direction: message.myMessage ? 'rtl' : 'intial' }">
                <div class="flex gap-3">
                    <div>
                        <img :src="message.sender.imageUrl" class="chat_user_avatar" />
                    </div>
                    <div :class="[message.myMessage ? 'my_message' : 'other_message', 'chat_message']">
                        <div class="text-xs" style="margin-bottom: 0.5em;">
                            {{ message.timestamp }}
                        </div>
                        {{ message.text }}
                    </div>
                </div>
            </div>
        </div>
    </ScrollPanel>

    
    <div v-if="canWriteComment" class="flex flex-row-reverse justify-content-center align-items-center gap-2 mt-2 mx-3">

        <Textarea v-model="comment" rows="1" style="width: 100%;direction: rtl;" :disabled="globalStore.loading"
            class="border-round-lg m-0" />
        <Button :disabled="!canSendComment()" @click="sendComment"
            class="flex justify-content-center align-items-center m-0 border-round-lg"
            style="padding-top: 0.75rem; padding-bottom: 0.75rem;">
            <i :class="['pi', 'pi-send']" />
        </Button>

    </div>
</template>

<script setup>
import { ref, onBeforeMount, onBeforeUnmount, inject, computed } from 'vue'
import { useGlobalStore } from '@/stores/global'
import { useAuthStore } from '@/stores/auth'
import { useToast } from "primevue/usetoast"
import { useI18n } from 'vue-i18n'
import { handleError } from '@/helpers/ErrorHandler'
import utils from '@/helpers/Utils'
import { genericServerMessageEnum, socketMessageTypeEnum } from '@/helpers/Constants'
import chatService from '@/service/ChatService'
import socketService from '@/service/SocketService'
import userService from '@/service/UserService'
import SocketListener from '@/model/SocketListener'

import Textarea from 'primevue/textarea';


const globalStore = useGlobalStore()
const authStore = useAuthStore()
const toast = useToast()
const i18n = useI18n()
const dialogRef = inject("dialogRef")

const creatorId = ref()
const inviteeId = ref()
const chatThread = ref([])
const comment = ref('')
let chatThreadId

const emit = defineEmits(['error'])

let uniqueId

onBeforeUnmount(() => {
    socketService.removeListener(uniqueId)
    globalStore.removeDialog(dialogRef.value)
})

onBeforeMount(async () => {
    chatThreadId = dialogRef.value.data.chatThreadId
    uniqueId = 'FREE_CHAT_' + new Date().getTime()
    socketService.addListener(socketMessageTypeEnum.FREE_CHAT_MESSAGE, new SocketListener(uniqueId, handleInstantMessage))
    globalStore.registerDialog(dialogRef.value)
    if (chatThreadId !== undefined) {
        try {
            const response = await chatService.getChatThread(chatThreadId)
            if (response.result.messages.length > 0) {
                creatorId.value = response.result.creatorId
                inviteeId.value = response.result.inviteeId
                chatThread.value = [...response.result.messages]
                prepareMessages(chatThread.value)
                scrollToLastMessage()
            }
        } catch (error) {
            handleError(authStore, toast, i18n, error)
        }
    }
})

const sendComment = async () => {
    if (comment.value?.trim()) {
        try {
            const response = await chatService.sendComment(comment.value, chatThreadId, dialogRef.value.data.receiverId)
            if (response.message === genericServerMessageEnum.OK) {
                comment.value = ''
                if (chatThread.value.length === 0) {
                    creatorId.value = authStore.user.id
                    chatThreadId = response.result.chatThreadId
                }
                pushMessage(response.result)
            }
        } catch (e) {
            emit('error', e)
        }
    }
}

const pushMessage = (message) => {
    prepareMessages([message])
    chatThread.value.push(message)
    scrollToLastMessage()
}

const handleInstantMessage = (message) => {
    if (message.chatThreadId === chatThreadId) {
        pushMessage(message)
    }
}

const scrollToLastMessage = () => {
    setTimeout(function () {
        const messagesNodes = document.getElementsByClassName("message_container")
        if (messagesNodes.length > 0) {
            const lastMessage = messagesNodes[messagesNodes.length - 1]
            lastMessage.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
        }
    }, 0)
}

const canWriteComment = computed(() => {
    const newChat = chatThread.value.length === 0
    const userInvolvedInChat = authStore.user.id === creatorId.value || authStore.user.id === inviteeId.value
    return newChat || userInvolvedInChat
})

const canSendComment = () => {
    return comment.value?.trim() && !globalStore.loading
}

const prepareMessages = (messages) => {
    for (const message of messages) {
        message.timestamp = utils.formatDate(message.timestamp)
        message.myMessage = authStore.user.username === message.sender.email
        userService.decorate([message.sender, message.receiver])
    }
}

const isEmptyChat = computed(() => {
    return chatThread.value.length === 0
})

</script>

<style scoped>

.chat_user_avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
}


.chat_message {
  width: 80%;
  border-radius: 1em;
  padding: 1em;
  direction: rtl;
  font-size: 14px;

}

.my_message {
    background-color: var(--blue-100);
    border: 1px solid var(--surface-border);
}

.other_message {
    background-color: var(--surface-50);
    border: 1px solid var(--surface-border);

}

.loading_chat_spinner {
    display: flex;
    justify-content: center;
    padding: 3em;
}
</style>